import dayjs from "dayjs";

import { CRON_IDENTITY } from "@/pages/tina/lib/cron-identity";
import type { IIdentity } from "@/providers/auth-provider";
import type { RunRead, TriggerSpec } from "@/types/tina";

const TRIGGER_TYPE_LABEL: Record<string, string> = {
  manual: "Manual",
  cron: "Cron",
};

const SITES_PREVIEW = 1;

const triggerSites = (spec: TriggerSpec): string[] =>
  spec && (spec.type === "manual" || spec.type === "cron")
    ? (spec.sites ?? [])
    : [];

/** Human label for "who triggered" — member name, else a trigger-type word, else em dash. */
export const triggeredByLabel = (
  triggeredBy: string | null | undefined,
  triggerType: string | null | undefined,
  members: IIdentity[],
): string => {
  if (triggeredBy) {
    const member = members.find((m) => m.id === triggeredBy);
    return member?.displayName ?? member?.email ?? triggeredBy;
  }
  // No human triggerer (cron / legacy / system) — name it by trigger type.
  if (triggerType && triggerType !== "manual") {
    return TRIGGER_TYPE_LABEL[triggerType] ?? triggerType;
  }
  return "—";
};

/** The identity to render as the run's triggerer avatar, or null to fall back
 *  to a text label. Cron runs (no human triggerer) resolve to CRON_IDENTITY. */
export const resolveTriggerActor = (
  triggeredBy: string | null | undefined,
  triggerType: string | null | undefined,
  members: IIdentity[],
): IIdentity | null => {
  if (triggeredBy) {
    const member = members.find((m) => m.id === triggeredBy);
    if (member) return member;
  }
  if (triggerType === "cron") return CRON_IDENTITY;
  return null;
};

/** Self-describing label for the run filter dropdown: "Manual · Eleni · livenation/gr +2 · Jun 25 14:32". */
export const runOptionLabel = (run: RunRead, members: IIdentity[]): string => {
  const type = TRIGGER_TYPE_LABEL[run.trigger_type] ?? run.trigger_type;
  const who = triggeredByLabel(run.triggered_by, run.trigger_type, members);
  const sites = triggerSites(run.trigger_spec);
  const sitesLabel =
    sites.length > 0
      ? `${sites.slice(0, SITES_PREVIEW).join(", ")}${sites.length > SITES_PREVIEW ? ` +${sites.length - SITES_PREVIEW}` : ""}`
      : "";
  const when = dayjs(run.created_at).format("MMM D HH:mm");
  return [type, who !== "—" ? who : null, sitesLabel || null, when]
    .filter((p): p is string => Boolean(p))
    .join(" · ");
};
